import { gql } from '@graphql/__generated__';

export const ActivityCellFragment = gql(/* GraphQL */ `
  fragment ActivityCell on activities {
    id
    created_at
    due_at
    done_at
    consolidated_done_at
    start_date
    end_date
    done
    success
    subject
    activity_type
    note
  }
`);

export const LeadsListRowActivitiesFragment = gql(/* GraphQL */ `
  fragment LeadsListRowActivities on leads {
    next_activity_assigned_to: activities(
      where: {
        done: { _eq: false }
        activity_type: { _in: [call, visit, task, assignment] }
        assigned_to: { _in: $userIds }
      }
      order_by: { consolidated_due_at: asc_nulls_last }
      limit: 1
    ) @include(if: $hasUserFilter) {
      id
      ...ActivityCell
    }
    last_activity_assigned_to: activities(
      where: {
        done: { _eq: true }
        activity_type: { _in: [call, visit, note, assignment, task] }
        consolidated_done_at: { _is_null: false }
        assigned_to: { _in: $userIds }
      }
      order_by: { consolidated_done_at: desc_nulls_last }
      limit: 1
    ) @include(if: $hasUserFilter) {
      id
      ...ActivityCell
    }
  }
`);

export const GET_LEADS = gql(/* GraphQL */ `
  query GetLeads(
    $limit: Int
    $offset: Int
    $where: leads_bool_exp
    $order_by: [leads_order_by!] = [{ requalified_or_created_at: asc }]
    $hasUserFilter: Boolean!
    $userIds: [uuid!]
  ) {
    leads(limit: $limit, offset: $offset, order_by: $order_by, where: $where) {
      id
      ...LeadsListItem
      ...LeadsListRowActivities
    }
  }
`);

export const SEARCH_LEADS_LIST = gql(/* GraphQL */ `
  query SearchLeadsList(
    $search: String!
    $limit: Int
    $offset: Int
    $where: leads_bool_exp
    $order_by: [leads_order_by!] = [{ requalified_or_created_at: asc }]
    $hasUserFilter: Boolean!
    $userIds: [uuid!]
  ) {
    leads: search_leads_tsv(
      args: { search_text: $search }
      limit: $limit
      offset: $offset
      order_by: $order_by
      where: $where
    ) {
      id
      ...LeadsListItem
      ...LeadsListRowActivities
    }
  }
`);

export const GET_KANBAN_LEADS_AGENTS = gql(/* GraphQL */ `
  query GetKanbanLeadsAgents(
    $limit: Int
    $offset: Int
    $where: lead_agents_bool_exp
    $where_leads: leads_bool_exp
    $include_leads: Boolean!
  ) {
    lead_agents(
      limit: $limit
      offset: $offset
      where: $where
      order_by: [
        { lead: { requalified_or_created_at: desc_nulls_last } }
        { created_at: desc }
      ]
    ) @skip(if: $include_leads) {
      id
      ...LeadsKanbanColumnItem
    }

    leads(
      limit: $limit
      offset: $offset
      order_by: [{ requalified_or_created_at: desc_nulls_last }]
      where: $where_leads
    ) @include(if: $include_leads) {
      id
      ...LeadsKanbanColumnLead
    }
  }
`);

export const GET_KANBAN_LEADS_COUNT = gql(/* GraphQL */ `
  query GetKanbanLeadsCount(
    $where: lead_agents_bool_exp
    $where_leads: leads_bool_exp
    $include_leads: Boolean!
    $limit: Int
  ) {
    lead_agents_aggregate(where: $where, limit: $limit)
      @skip(if: $include_leads) {
      aggregate {
        count
      }
    }

    leads_aggregate(where: $where_leads, limit: $limit)
      @include(if: $include_leads) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_LEADS_COUNT = gql(/* GraphQL */ `
  query GetLeadsCount($where: leads_bool_exp, $limit: Int) {
    leads_aggregate(where: $where, limit: $limit) {
      aggregate {
        count
      }
    }
  }
`);

export const SEARCH_LEADS_LIST_COUNT = gql(/* GraphQL */ `
  query SearchLeadsListCount(
    $search: String!
    $where: leads_bool_exp
    $limit: Int
  ) {
    leads_aggregate: search_leads_tsv_aggregate(
      args: { search_text: $search }
      where: $where
      limit: $limit
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_LEAD = gql(/* GraphQL */ `
  query GetLead($lead_id: uuid!) {
    leads_by_pk(id: $lead_id) {
      id
      tenant_id
      ...MarketingCardLeadFragment
      appraisal_reason
      appraisal_perception
      appraisal_perceived_value
      property {
        id
        latest_appraisal {
          id
          value
        }
      }
      lots {
        id
      }
      lead_agents(where: { passed_at: { _is_null: true } }) {
        id
        user {
          ...UserCard_user
          default_team {
            id
            name
          }
        }
      }
      contact {
        id
        ...ContactCardUserFragment
      }
    }
    lead_stages(
      where: {
        pipeline: { name: { _eq: "brokerage" } }
        name: { _eq: "assign" }
      }
      limit: 1
    ) {
      id
    }
  }
`);

export const LEAD_FEED = gql(/* GraphQL */ `
  query LeadFeed($lead_id: uuid!) {
    activities: get_activities_feed_for_lead(args: { p_lead_id: $lead_id }) {
      ...ActivityItem
      ...ActivityFiltersActivity
    }
  }
`);

export const OLD_LEAD_FEED = gql(/* GraphQL */ `
  query OldLeadFeed($lead_id: uuid!) {
    activities: get_activities_feed_for_lead(args: { p_lead_id: $lead_id }) {
      ...activityFields
    }
  }
`);

export const LEAD_AGENT_FRAGMENT = gql(/* GraphQL */ `
  fragment LeadAgent on lead_agents {
    id
    created_at
    claimed_at
    passed_at
    source
    win_probability
    predicted_win_date
    lead_id
    lead_stage {
      id
      name
      label
      status
      pipeline {
        id
      }
    }
    user {
      id
      ...UserAvatar
      first_name
      last_name
      default_team {
        id
        name
      }
    }
  }
`);

export const GET_LEAD_AGENTS = gql(/* GraphQL */ `
  query GetLeadAgents($lead_id: uuid!) {
    leads_by_pk(id: $lead_id) {
      owner_team_id
    }
    lead_agents(
      where: { lead_id: { _eq: $lead_id } }
      order_by: { created_at: asc }
    ) {
      id
      ...LeadAgent
    }
  }
`);

export const ADD_LEAD_AGENT = gql(/* GraphQL */ `
  mutation AddLeadAgent(
    $lead_id: uuid!
    $user_id: uuid!
    $stage_id: uuid
    $activity_object: activities_insert_input!
  ) {
    insert_lead_agents_one(
      object: {
        lead_id: $lead_id
        user_id: $user_id
        source: crm
        passed_at: null
        stage_id: $stage_id
      }
      on_conflict: {
        constraint: lead_agents_lead_id_user_id_source_key
        update_columns: [passed_at, stage_id]
      }
    ) {
      id
    }
    insert_activities_one(object: $activity_object) {
      id
    }
  }
`);

export const DELETE_LEAD_AGENT = gql(/* GraphQL */ `
  mutation DeleteLeadAgent($id: uuid!, $lead_id: uuid!, $user_id: uuid!) {
    delete_lead_agents_by_pk(id: $id) {
      id
    }

    update_activities(
      where: {
        lead_id: { _eq: $lead_id }
        activity_type: { _eq: assignment }
        done: { _eq: false }
        assigned_to: { _eq: $user_id }
      }
      _set: { done: true, success: false, done_at: "now()" }
    ) {
      affected_rows
    }
  }
`);

export const UPDATE_LEAD_AGENTS = gql(/* GraphQL */ `
  mutation UpdateLeadAgents(
    $where: lead_agents_bool_exp!
    $set: lead_agents_set_input!
  ) {
    update_lead_agents(where: $where, _set: $set) {
      returning {
        id
        ...LeadAgent
      }
    }
  }
`);

export const GET_BROKERAGE_PIPELINE = gql(/* GraphQL */ `
  query GetBrokeragePipeline {
    pipelines(where: { name: { _eq: "brokerage" } }, limit: 1) {
      id
      stages(order_by: { order_nr: asc }) {
        id
        label
        status
        name
        pipeline {
          id
          lead_type
        }
      }
    }
  }
`);

export const GET_PIPELINE_STAGES = gql(/* GraphQL */ `
  query GetPipelineStages($pipeline_id: uuid!) {
    pipelines_by_pk(id: $pipeline_id) {
      id
      stages(order_by: { order_nr: asc }) {
        id
        label
        status
        name
        pipeline {
          id
          lead_type
        }
      }
    }
  }
`);

export const GET_LEAD_AGENT_SELECT_CARD_QUERY = gql(/* GraphQL */ `
  query GetLeadAgentSelectCard($lead_id: uuid!) {
    leads_by_pk(id: $lead_id) {
      owner_team_id
    }
    lead_agents(
      where: { lead_id: { _eq: $lead_id } }
      order_by: [{ created_at: asc }, { id: asc }]
    ) {
      id
      lead_id
      source
      passed_at
      user {
        id
        ...UserCard_user
        default_team {
          id
          name
        }
      }
      lead_stage {
        id
        pipeline_id
      }
    }
  }
`);

export const ASSIGN_BROKER = gql(/* GraphQL */ `
  mutation AssignBroker(
    $broker_id: uuid!
    $lead_id: uuid!
    $team_ids: [uuid!]
    $stage_id: uuid
    $activity_object: activities_insert_input!
    $reassignment: lead_agents_updates!
    $keep_tasks: activities_bool_exp!
  ) {
    update_activities(
      where: {
        _and: [
          {
            lead_id: { _eq: $lead_id }
            activity_type: { _eq: assignment }
            done: { _eq: false }
            assignee: { teams_users: { team_id: { _in: $team_ids } } }
          }
          $keep_tasks
        ]
      }
      _set: { done: true, success: false, done_at: "now()" }
    ) {
      affected_rows
    }

    insert_activities_one(object: $activity_object) {
      id
    }

    insert_lead_agents_one(
      object: {
        lead_id: $lead_id
        user_id: $broker_id
        stage_id: $stage_id
        source: crm
        passed_at: null
      }
      on_conflict: {
        constraint: lead_agents_lead_id_user_id_source_key
        update_columns: [passed_at, stage_id]
      }
    ) {
      id
    }

    delete_lead_agents(
      where: {
        lead_id: { _eq: $lead_id }
        user: {
          teams_users: { team_id: { _in: $team_ids } }
          id: { _neq: $broker_id }
        }
      }
    ) {
      affected_rows
    }

    update_lead_agents_many(updates: [$reassignment]) {
      affected_rows
    }
  }
`);
